import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  IFilter,
  IGrupo,
  IQueryParam,
  IRecorrido,
  ITracker,
  IDashcam,
  IUsuario,
  IActivo,
  ICreateActivo,
  IUpdateActivo,
  ICliente,
  IReporteBase,
  IReporteGenerico,
} from 'modelos/src';
import { Subscription, firstValueFrom } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { ActivosService } from '../../../../../auxiliares/servicios/http/activos.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ParamsService } from '../../../../../auxiliares/servicios/params.service';
import { CrearEditarAgrupacionesComponent } from '../../../../standalone/grupos/crear-editar-grupos/crear-editar-grupos.component';
import { CrearEditarTrackerComponent } from '../../../administracion/trackers/crear-editar-tracker/crear-editar-tracker.component';
import {
  CrearEditarDashcamComponent,
  ICrearEditarDashcamData,
} from '../../../administracion/dashcams/crear-editar-dashcam/crear-editar-dashcam.component';
import { CrearEditarRecorridoComponent } from '../../recorridos/crear-editar-recorrido/crear-editar-recorrido.component';
import {
  LineStringMapa,
  MapaFacilData,
  MarkerMapa,
} from '../../../../../auxiliares/mapa-facil/interfaces';
import { LoginService } from '../../../../login/login.service';
import {
  CrearEditarUsuarioFuncionComponent,
  DataUsuarioFuncion,
} from '../../../../standalone/usuarios-por-funcion/crear-editar-usuario-funcion/crear-editar-usuario-funcion.component';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { MapaFacilService } from '../../../../../auxiliares/mapa-facil/mapa-facil.service';
import { ChequeoIdentificacion } from '../../../../../auxiliares/servicios/chequeo-identificacion';
import {
  revalidarTrackerAlCambiarDashcam,
  trackerODashcamRequerido,
} from '../../../../../auxiliares/tracker-o-dashcam.util';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';

@Component({
  selector: 'app-crear-editar-colectivo',
  templateUrl: './crear-editar-colectivo.component.html',
  styleUrl: './crear-editar-colectivo.component.scss',
  standalone: false,
})
export class CrearEditarColectivoComponent implements OnInit, OnDestroy {
  private formularios = inject(FormularioDialogService);
  dialogRef = inject<MatDialogRef<CrearEditarColectivoComponent>>(
    MatDialogRef,
    { optional: true },
  );
  helper = inject(HelperService);
  private route = inject(ActivatedRoute);
  private service = inject(ActivosService);
  private lista2 = inject(Lista2Service);
  private dialog = inject(MatDialog);
  paramsService = inject(ParamsService);
  private mapaService = inject(MapaFacilService);

  // El pie está fuera del @if del formulario: el stepper se toma por ViewChild
  @ViewChild('stepper') stepper?: MatStepper;

  public clientePropio = LoginService.getCliente();
  public loading = false;
  public form?: FormGroup;
  public title?: string;
  public subtitle?: string;
  public id?: string | null;
  public vehiculo?: IActivo;
  public mapaFacil: MapaFacilData = {};

  public trackers?: ITracker[];
  public trackers$?: Subscription;
  public dashcams?: IDashcam[];
  public dashcams$?: Subscription;

  public recorridos?: IRecorrido[];
  public recorridos$?: Subscription;

  public reporte?: IReporteBase<'Tracker 4G'>;
  public reportes$?: Subscription;

  public estados = ['Operativo', 'En mantenimiento', 'Fuera de servicio'];

  public tipoCliente = HelperService.getTipoCliente();
  get vehiculoForm() {
    return this.form?.get('vehiculo') as FormGroup;
  }

  get idCliente() {
    return this.form?.get('idCliente')?.value;
  }

  public chequeo = new ChequeoIdentificacion<IActivo>({
    entidad: 'colectivo',
    articulo: 'un',
    campo: 'identificacion',
    control: () => this.form?.get('identificacion'),
    listar: (query) => this.service.getFiltered(query),
    idCliente: () => this.idCliente,
    excluirId: () => this.id,
    filtroExtra: () => ({ categoria: 'Colectivo' }),
  });

  public createForm() {
    const clienteDefault =
      this.tipoCliente === 'Final'
        ? this.clientePropio._id
        : (this.vehiculo?.idCliente ?? this.clientePropio?._id);

    this.form = new FormGroup({
      identificacion: new FormControl(
        this.vehiculo?.identificacion,
        Validators.required,
        this.chequeo.validador,
      ),
      idsClientesQuePuedenAtender: new FormControl(
        this.vehiculo?.idsClientesQuePuedenAtender ?? null,
      ),
      idsClientesQuePuedenAtenderEventosTecnicos: new FormControl(
        this.vehiculo?.idsClientesQuePuedenAtenderEventosTecnicos ?? null,
      ),
      configHorariosAtencion: new FormControl(
        this.vehiculo?.configHorariosAtencion ??
          (this.clientePropio?._id
            ? [{ idCliente: this.clientePropio._id }]
            : null),
      ),
      configHorariosAtencionTecnica: new FormControl(
        this.vehiculo?.configHorariosAtencionTecnica ??
          (this.clientePropio?._id
            ? [{ idCliente: this.clientePropio._id }]
            : null),
      ),
      puedeSolicitarServicioTecnico: new FormControl(
        this.vehiculo?.puedeSolicitarServicioTecnico ?? false,
      ),
      idGrupo: new FormControl(this.vehiculo?.idGrupo),
      // Tracker o dashcam: alcanza con una de las dos fuentes de posición.
      idTracker: new FormControl(
        this.vehiculo?.idTracker,
        trackerODashcamRequerido,
      ),
      idDashcam: new FormControl(this.vehiculo?.idDashcam),
      idCliente: new FormControl(clienteDefault, Validators.required),
      categoria: new FormControl('Colectivo'),
      funcion: new FormControl('Transporte'),
      vehiculo: new FormGroup({
        idExterno: new FormControl(this.vehiculo?.vehiculo?.idExterno),
        idChofer: new FormControl(this.vehiculo?.vehiculo?.idChofer),
        idRecorrido: new FormControl(this.vehiculo?.vehiculo?.idRecorrido),
        idsRecorridos: new FormControl(this.vehiculo?.vehiculo?.idsRecorridos),
        patente: new FormControl(this.vehiculo?.vehiculo?.patente),
        estado: new FormControl(this.vehiculo?.vehiculo?.estado),
        marca: new FormControl(this.vehiculo?.vehiculo?.marca),
        modelo: new FormControl(this.vehiculo?.vehiculo?.modelo),
        anio: new FormControl(this.vehiculo?.vehiculo?.anio),
        tipo: new FormControl('Colectivo'),
      }),
    });
  }

  public async onSubmit() {
    this.loading = true;
    try {
      if (!(await this.chequeo.puedeGuardar())) {
        this.helper.notifError(`${this.chequeo.textoRepetida} Elegí otra.`);
        this.loading = false;
        return;
      }

      if (this.id) {
        // Editar
        const data = this.getData();
        if (data) {
          await this.service.update(this.id, data);
          this.helper.notifSuccess('Vehículo actualizado correctamente');
          this.helper.volver();
        }
      } else {
        // Crear
        const data = this.getData();
        if (data) {
          await this.service.create(data);
          this.helper.notifSuccess('Vehículo creado correctamente');
          this.helper.volver();
        }
      }
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.loading = false;
  }

  private setTitles() {
    if (this.id) {
      this.title = 'Editar Colectivo';
      this.subtitle = this.vehiculo?.identificacion;
    } else {
      this.title = 'Crear Colectivo';
    }
  }

  public async onClienteChange(cliente: ICliente) {
    // El mensaje de identificación repetida aclara el cliente: revalidar.
    this.chequeo.revalidar();

    // Resetea el form
    this.form.patchValue({ idTracker: null, idDashcam: null });
    this.form.patchValue({ idsClientesQuePuedenAtenderEventosTecnicos: null });

    this.form.patchValue({ puedeSolicitarServicioTecnico: false });
    this.form.patchValue({ idGrupo: null });
    this.form.patchValue({ idRecorrido: null });
    this.vehiculoForm.patchValue({ idChofer: null });

    // Filtrar horarios: conservar solo clientePropio si estaba seleccionado
    const propioId = this.clientePropio?._id;
    for (const ctrl of [
      'configHorariosAtencion',
      'configHorariosAtencionTecnica',
    ]) {
      const current = this.form.get(ctrl)?.value;
      if (current?.length) {
        const filtered = current.filter((c: any) => c.idCliente === propioId);
        this.form.get(ctrl)?.patchValue(filtered.length ? filtered : null);
      }
    }

    // Carga las cosas del nuevo cliente
    if (cliente) {
      this.listarDashcams();
      await Promise.all([this.listarRecorridos(), this.listarTrackers()]);
    } else {
      this.recorridos = [];
      this.trackers = [];
      this.dashcams = [];
    }
  }

  private getData() {
    const data: ICreateActivo | IUpdateActivo = this.form?.value;
    data.categoria = 'Colectivo';
    const idsAtencion = (data.configHorariosAtencion ?? [])
      .map((c: any) => c.idCliente)
      .filter(Boolean) as string[];
    const idsTecnicos = (data.configHorariosAtencionTecnica ?? [])
      .map((c: any) => c.idCliente)
      .filter(Boolean) as string[];
    data.idsClientesQuePuedenAtender = idsAtencion;
    data.idsClientesQuePuedenAtenderEventosTecnicos = idsTecnicos.length
      ? idsTecnicos
      : null;
    return data;
  }

  public async listar() {
    const res = await firstValueFrom(this.lista2.vehiculo(this.id!));
    if (res.vehiculo.tipo !== 'Colectivo') {
      this.helper.notifError('El vehículo no es un colectivo');
      this.helper.volver();
      return;
    }
    this.vehiculo = res;
  }

  public async listarRecorridos() {
    if (this.form?.value?.idCliente) {
      const filter: IFilter<IRecorrido> = {
        idCliente: this.form.value.idCliente,
      };
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        sort: 'nombre',
        select: 'nombre idCliente geojson',
        limit: 0,
        includeChildren: true,
      };
      this.recorridos$?.unsubscribe();
      this.recorridos$ = this.lista2.recorridos(query, 30).subscribe((data) => {
        this.recorridos = data.datos;
        this.changeRecorridos();
      });
    }
  }

  public async listarTrackers() {
    if (this.form?.value?.idCliente) {
      const filter: IFilter<ITracker> = {
        // El tracker de un teléfono (tracking GPS de la app) también puede ir
        // en un colectivo: el backend lo reporta con la forma de un 4G.
        tipo: { $in: ['GPRS', 'Telefono'] },
        $and: [
          { idCliente: this.form?.value?.idCliente },
          {
            $or: [
              {
                asignadoA: null,
              },
              {
                asignadoA: { $exists: false },
              },
            ],
          },
        ],
      };
      if (this.vehiculo?._id) {
        filter.$and[1].$or.push({
          _id: this.vehiculo?.idTracker,
        });
      }
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        sort: 'identificacion',
        limit: 0,
        includeChildren: true,
      };
      //
      this.trackers$?.unsubscribe();
      this.trackers$ = this.lista2.trackers(query, 30).subscribe((data) => {
        const trackers = JSON.parse(JSON.stringify(data.datos));
        this.trackers = trackers;
      });
    }
  }
  /** Dashcams del cliente sin asignar, más la del colectivo en edición. */
  public listarDashcams(): void {
    const idCliente = this.form?.value?.idCliente;
    this.dashcams$?.unsubscribe();
    if (!idCliente) {
      this.dashcams = [];
      return;
    }
    const libres: IFilter<IDashcam>[] = [
      { asignadoA: null },
      { asignadoA: { $exists: false } },
    ];
    if (this.vehiculo?.idDashcam) libres.push({ _id: this.vehiculo.idDashcam });
    const filter: IFilter<IDashcam> = {
      $and: [{ idCliente }, { $or: libres }],
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      select: 'nombre identificacion uniqueId',
      sort: 'nombre',
      limit: 0,
      includeChildren: true,
    };
    this.dashcams$ = this.lista2.dashcams(query, 30).subscribe((data) => {
      this.dashcams = data.datos;
    });
  }

  /** Alta de una dashcam en el cliente del colectivo; vuelve ya elegida. */
  public crearDashcam(): void {
    const idCliente = this.form?.value?.idCliente;
    if (!idCliente) return;
    this.formularios
      .abrir<CrearEditarDashcamComponent, ICrearEditarDashcamData, IDashcam>(
        CrearEditarDashcamComponent,
        { tamano: 'grande', data: { idCliente } },
      )
      .afterClosed()
      .subscribe((res) => {
        if (!res?._id) return;
        this.listarDashcams();
        this.form.patchValue({ idDashcam: res._id });
      });
  }

  private async listarUltimoReporte() {
    if (!this.id) {
      return;
    }
    const filter: IFilter<IReporteGenerico> = {
      idsAsignados: this.id,
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: '-fechaCreacion',
      select: 'valores geojson',
      includeChildren: true,
    };
    this.reportes$?.unsubscribe();
    this.reportes$ = this.lista2
      .ultimosReportesGenericos<'Tracker 4G'>(query, 30)
      .subscribe((data) => {
        this.reporte = data.datos[0];
        this.addVehiculoMapa();
      });
  }

  public crearTracker() {
    this.paramsService.setParams({
      desdeActivo: true,
      idCliente: this.form.value.idCliente,
    });
    const dialog = this.formularios.abrir(CrearEditarTrackerComponent, {
      tamano: 'grande',
    });
    dialog.afterClosed().subscribe((res) => {
      if (res) {
        this.form.patchValue({ idTracker: res });
      }
    });
  }
  public crearRecorrido() {
    this.formularios.abrir(CrearEditarRecorridoComponent, { tamano: 'grande' });
  }
  public crearChofer() {
    const data: DataUsuarioFuncion = {
      editando: false,
      idCliente: this.form.value.idCliente,
      funcion: 'Chofer',
    };
    const dialog = this.formularios.abrir(CrearEditarUsuarioFuncionComponent, {
      tamano: 'grande',
      data,
    });
    dialog.afterClosed().subscribe((res) => {
      if (res) {
        this.vehiculoForm.patchValue({ idChofer: res });
      }
      this.paramsService.setParams({ vehiculo: this.vehiculo });
    });
    dialog.afterClosed().subscribe((res) => {
      if (res) {
        this.form.patchValue({ idTracker: res });
      }
    });
  }

  public changeRecorridos() {
    const ids = this.form?.value?.vehiculo?.idsRecorridos;
    const data: MapaFacilData = {
      polylines: [],
    };
    if (ids) {
      const recorridos = this.recorridos?.filter((r) => ids.includes(r._id));
      if (recorridos) {
        data.polylines.push(
          ...recorridos.map((r) => this.armarRecorridoMapa(r)),
        );
      }
    }
    const idActual = this.form?.value?.vehiculo?.idRecorrido;
    if (idActual) {
      const recorridoActual = this.recorridos?.find((r) => r._id === idActual);
      if (recorridoActual) {
        const recorridoMapa = this.armarRecorridoMapa(recorridoActual, true);
        data.polylines.push(recorridoMapa);
      }
    }
    this.mapaService.updateData(data);
  }

  private armarRecorridoMapa(r: IRecorrido, actual?: boolean) {
    const recorridoMapa: LineStringMapa = {
      points: r.geojson.coordinates as [number, number][],
      color: actual ? 'blue' : 'grey',
      width: 5,
      arrow: actual ? true : false,
      centrar: false,
    };
    return recorridoMapa;
  }

  private addVehiculoMapa() {
    if (!this.reporte || !this.reporte?.valores?.geojson) {
      return;
    }
    const data: MapaFacilData = {
      markers: [],
    };
    const marker: MarkerMapa = {
      coor: this.reporte.valores.geojson.coordinates as [number, number],
      label: 'vehiculo',
      text: this.vehiculo?.identificacion,
      centrar: true,
    };
    data.markers = [marker];
    this.mapaService.updateData(data);
  }

  /// HOOKS

  async ngOnInit() {
    this.loading = true;

    // Intenta obtener el ID desde la ruta o desde los datos del diálogo
    const params = await firstValueFrom(this.route.paramMap);
    this.id = params.get('id') || null;
    if (this.id) {
      await this.listar();
    }
    this.createForm();
    revalidarTrackerAlCambiarDashcam(this.form);
    this.setTitles();

    // Preseleccionar el cliente del permiso activo al crear
    if (!this.id) {
      this.form.patchValue({
        idCliente: this.clientePropio?._id,
      });
    }

    this.listarDashcams();
    await Promise.all([
      this.listarTrackers(),
      this.listarRecorridos(),
      this.listarUltimoReporte(),
    ]);
    this.changeRecorridos();
    this.loading = false;
  }

  ngOnDestroy(): void {
    this.recorridos$?.unsubscribe();
    this.trackers$?.unsubscribe();
    this.dashcams$?.unsubscribe();
    this.reportes$?.unsubscribe();
  }
}
